// src/pages/PageMenu/AccountsMenu.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import {
  Container,
  Grid,
  Typography,
  ButtonBase,
  Paper,
  useTheme,
  useMediaQuery,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SettingsIcon from '@mui/icons-material/Settings';

import PostAddIcon from '@mui/icons-material/PostAdd';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalculateIcon from '@mui/icons-material/Calculate';

export default function AccountsMenu() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isRTL = i18n.language === 'ar';

  // 1. Definitions (التعاريف)
  const definitionsItems = [
    // Chart of Accounts (دليل الحسابات)
    hasPermission(PERMISSIONS.CHART_OF_ACCOUNTS_VIEW) && {
      name: "دليل الحسابات", 
      icon: <AccountTreeIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#673ab7" }} />,
      link: "/chart-of-accounts"
    },
    // GL Settings (توجيه الحسابات)
    hasPermission(PERMISSIONS.GL_SETTINGS_VIEW) && {
      name: "توجيه الحسابات",
      icon: <SettingsIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#607d8b" }} />,
      link: "/gl-settings"
    },
    // Safes (الصناديق)
    hasPermission(PERMISSIONS.SAFE_VIEW) && {
      name: "الصناديق",
      icon: <AccountBalanceWalletIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#795548" }} />,
      link: "/safes"
    },
    // Banks (البنوك)
    hasPermission(PERMISSIONS.BANK_VIEW) && {
      name: "البنوك",
      icon: <AccountBalanceIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#3f51b5" }} />,
      link: "/banks"
    },
    // Payment Methods (طرق الدفع)
    hasPermission(PERMISSIONS.PAYMENT_METHOD_VIEW) && {
      name: "طرق الدفع",
      icon: <PaymentIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#e91e63" }} />,
      link: "/payment-methods"
    },
  ].filter(Boolean);

  // 2. Operations (العمليات)
  const operationsItems = [
    // Opening Entry (قيد افتتاحي)
    hasPermission(PERMISSIONS.JOURNAL_ENTRY_CREATE) && {
      name: "قيد افتتاحي",
      icon: <PostAddIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#607d8b" }} />,
      link: "/journal-entries/new?type=2"
    },
    // Journal Entry (سند قيد)
    hasPermission(PERMISSIONS.JOURNAL_ENTRY_CREATE) && {
      name: "سند قيد",
      icon: <DescriptionIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#ff9800" }} />,
      link: "/journal-entries/new?type=1"
    },
    // Receipt Voucher (سند قبض)
    hasPermission(PERMISSIONS.BOND_RECEIPT_CREATE) && {
      name: "سند قبض",
      icon: <ReceiptIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#4caf50" }} />,
      link: "/vouchers/general-receipt"
    },
    // Payment Voucher (سند صرف)
    hasPermission(PERMISSIONS.BOND_PAYMENT_CREATE) && {
      name: "سند صرف",
      icon: <PaymentIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#f44336" }} />,
      link: "/vouchers/general-payment"
    },
    // Customer Receipt (سند قبض عميل)
    hasPermission(PERMISSIONS.BOND_RECEIPT_CREATE) && {
      name: "سند قبض عميل",
      icon: <RequestQuoteIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/vouchers/customer-receipt"
    },
    // Supplier Payment (سند صرف مورد)
    hasPermission(PERMISSIONS.BOND_PAYMENT_CREATE) && {
      name: "سند صرف مورد",
      icon: <ReceiptLongIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#e91e63" }} />,
      link: "/vouchers/supplier-payment"
    },
    // Tax Entry (سند ضريبي)
    hasPermission(PERMISSIONS.JOURNAL_ENTRY_CREATE) && {
      name: "سند ضريبي",
      icon: <CalculateIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#9c27b0" }} />,
      link: "/vouchers/tax-payment"
    }
  ].filter(Boolean);

  // 3. Reports (التقارير)
  const reportsItems = [
    // Vouchers List (قائمة السندات)
    hasPermission(PERMISSIONS.VOUCHER_VIEW) && {
      name: "قائمة السندات",
      icon: <ListAltIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#2196f3" }} />,
      link: "/vouchers-list"
    },
    // Journal Entries List (القيود اليومية)
    hasPermission(PERMISSIONS.JOURNAL_ENTRY_VIEW) && {
      name: "القيود اليومية",
      icon: <DescriptionIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#ff9800" }} />,
      link: "/journal-entries"
    },
    // Account Statement (كشف حساب)
    hasPermission(PERMISSIONS.ACCOUNT_STATEMENT_VIEW) && {
      name: "كشف حساب",
      icon: <AssessmentIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#e91e63" }} />,
      link: "/reports/account-statement"
    },
    // Trial Balance (ميزان المراجعة)
    hasPermission(PERMISSIONS.TRIAL_BALANCE_VIEW) && {
      name: "ميزان المراجعة",
      icon: <AccountBalanceIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#9c27b0" }} />,
      link: "/reports/trial-balance"
    },
    // Balance Sheet (الميزانية العمومية)
    hasPermission(PERMISSIONS.BALANCE_SHEET_VIEW) && {
      name: "الميزانية العمومية",
      icon: <AccountBalanceWalletIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#00bcd4" }} />,
      link: "/reports/balance-sheet"
    },
    // Income Statement (قائمة الدخل)
    hasPermission(PERMISSIONS.INCOME_STATEMENT_VIEW) && {
      name: "قائمة الدخل",
      icon: <TrendingUpIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#4caf50" }} />,
      link: "/reports/income-statement"
    },
    // Tax Declaration (الاقرار الضريبي)
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: "الاقرار الضريبي",
      icon: <DescriptionIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/VatReportPage"
    }
  ].filter(Boolean);

  const renderGridItems = (items) => (
    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
      {items.map((item, index) => (
        <Grid item xs={6} sm={4} md={3} lg={3} key={index}>
          <ButtonBase
            onClick={() => navigate(item.link)}
            sx={{
              width: "100%",
              height: { xs: 120, sm: 130, md: 140 },
              borderRadius: { xs: 2, sm: 2.5, md: 3 },
              border: "2px solid #e0e0e0",
              backgroundColor: "#ffffff",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: { xs: "translateY(-3px)", sm: "translateY(-4px)", md: "translateY(-5px)" },
                boxShadow: { xs: "0 6px 20px rgba(0,0,0,0.12)", sm: "0 8px 25px rgba(0,0,0,0.15)", md: "0 10px 30px rgba(0,0,0,0.18)" },
                borderColor: "#2196f3",
                backgroundColor: "#f8fafc",
              },
              "&:active": {
                transform: "translateY(0px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
              }
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              p={{ xs: 1.5, sm: 2, md: 2.5 }}
              textAlign="center"
            >
              {item.icon}
              <Typography
                variant="body1"
                fontWeight="600"
                mt={{ xs: 1, sm: 1.5 }}
                color="text.primary"
                sx={{
                  fontSize: {
                    xs: '0.85rem',
                    sm: '0.95rem',
                    md: '1rem'
                  },
                  lineHeight: 1.3,
                  px: 1
                }}
              >
                {item.name}
              </Typography>
            </Box>
          </ButtonBase>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container 
      maxWidth={false}
      sx={{ 
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }} 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <Paper 
       elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 3, sm: 4, md: 5 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 2, sm: 3, md: 3.5 },
          }}
        >
          <Typography 
            variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} 
            fontWeight="bold" 
            color="white"
            sx={{
              fontSize: {
                xs: '1.5rem',
                sm: '2rem',
                md: '2.5rem'
              },
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            {t('accountsMenu.title')}
          </Typography>
        </Box>
      </Paper>

      {/* 1. Definitions Section */}
      {definitionsItems.length > 0 && (
        <Paper 
          elevation={isMobile ? 3 : 4} 
          sx={{ 
            p: { xs: 2.5, sm: 3, md: 4 }, 
            mb: { xs: 2, sm: 3, md: 4 }, 
            borderRadius: { xs: 2, sm: 2.5, md: 3 },
            border: '1px solid #e3f2fd',
          }}
        >
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            fontWeight="bold" 
            mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
            color="primary"
            sx={{
              fontSize: {
                xs: '1.15rem',
                sm: '1.35rem',
                md: '1.5rem'
              },
              borderBottom: '3px solid #673ab7',
              paddingBottom: 1.5,
            }}
          >
            🗂️ التعاريف
          </Typography>
          {renderGridItems(definitionsItems)}
        </Paper>
      )}

      {/* 2. Operations Section */}
      {operationsItems.length > 0 && (
        <Paper 
          elevation={isMobile ? 3 : 4} 
          sx={{ 
            p: { xs: 2.5, sm: 3, md: 4 }, 
            mb: { xs: 2, sm: 3, md: 4 }, 
            borderRadius: { xs: 2, sm: 2.5, md: 3 },
            border: '1px solid #e3f2fd',
          }}
        >
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            fontWeight="bold" 
            mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
            color="primary"
            sx={{
              fontSize: {
                xs: '1.15rem',
                sm: '1.35rem',
                md: '1.5rem'
              },
              borderBottom: '3px solid #2196f3',
              paddingBottom: 1.5,
            }}
          >
            💰 العمليات
          </Typography>
          {renderGridItems(operationsItems)}
        </Paper>
      )}

      {/* 3. Reports Section */}
      {reportsItems.length > 0 && (
        <Paper 
          elevation={isMobile ? 3 : 4} 
          sx={{ 
            p: { xs: 2.5, sm: 3, md: 4 }, 
            mb: { xs: 2, sm: 3, md: 4 }, 
            borderRadius: { xs: 2, sm: 2.5, md: 3 },
            border: '1px solid #e3f2fd',
          }}
        >
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            fontWeight="bold" 
            mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
            color="primary"
            sx={{
              fontSize: {
                xs: '1.15rem',
                sm: '1.35rem',
                md: '1.5rem'
              },
              borderBottom: '3px solid #e91e63',
              paddingBottom: 1.5,
            }}
          >
            📊 التقارير
          </Typography>
          {renderGridItems(reportsItems)}
        </Paper>
      )}
    </Container>
  );
}
